
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Box,
  Typography,
  Alert,
  IconButton,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Close } from '@mui/icons-material'

import { AppDispatch, RootState } from '../../store'
import { createProject } from '../../store/slices/projectSlice'
import { ProjectForm } from '../../types'

interface CreateProjectDialogProps {
  open: boolean
  onClose: () => void
}

const CreateProjectDialog: React.FC<CreateProjectDialogProps> = ({ open, onClose }) => {
  const dispatch = useDispatch<AppDispatch>()
  const { loading, error } = useSelector((state: RootState) => state.projects)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ProjectForm>({
    defaultValues: {
      type: 'software',
    },
  })

  const projectName = watch('name')
  const keyField = register('key', {
    required: 'Project key is required',
    pattern: {
      value: /^[A-Z]{2,10}$/,
      message: 'Key must be 2-10 uppercase letters',
    },
  })

  // Auto-generate project key from name
  const generateKey = (name: string): string => {
    return name
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '')
      .split(' ')
      .map(word => word.slice(0, 2))
      .join('')
      .slice(0, 6)
  }

  const onSubmit = async (data: ProjectForm) => {
    try {
      const projectData = {
        ...data,
        key: data.key || generateKey(data.name),
      }

      const result = await dispatch(createProject(projectData))
      if (createProject.fulfilled.match(result)) {
        handleClose()
      }
    } catch (error) {
      console.error('Error creating project:', error)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const projectTypes = [
    { value: 'software', label: 'Software Development', description: 'Plan, track, and release software' },
    { value: 'business', label: 'Business Project', description: 'Manage business processes and workflows' },
    { value: 'service_desk', label: 'Service Desk', description: 'Handle customer requests and support' },
  ]

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Create New Project
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Project Name */}
            <TextField
              {...register('name', {
                required: 'Project name is required',
                minLength: { value: 2, message: 'Name must be at least 2 characters' },
                maxLength: { value: 100, message: 'Name must be less than 100 characters' },
              })}
              size="small"
              fullWidth
              label="Project Name"
              placeholder="Enter your project name"
              error={!!errors.name}
              helperText={errors.name?.message}
              autoFocus
            />

            {/* Project Key */}
            <TextField
              {...keyField}
              size="small"
              fullWidth
              label="Project Key"
              placeholder={projectName ? generateKey(projectName) : 'PROJ'}
              error={!!errors.key}
              helperText={errors.key?.message || 'Short identifier for your project (e.g., PROJ, WEB, MOB)'}
              inputProps={{ 
                style: { textTransform: 'uppercase' },
                maxLength: 10
              }}
              onChange={(e) => {
                // Convert to uppercase and remove any non-letter characters
                const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '')
                // Update the event target value with cleaned value
                e.target.value = value
                // Call the original onChange from register to update form state
                keyField.onChange(e)
              }}
            />

            {/* Project Type */}
            <TextField
              {...register('type', { required: 'Project type is required' })}
              size="small"
              select
              fullWidth
              label="Project Type"
              error={!!errors.type}
              helperText={errors.type?.message}
            >
              {projectTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <Box>
                    <Typography variant="body1">{type.label}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {type.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            {/* Description */}
            <TextField
              {...register('description', {
                maxLength: { value: 500, message: 'Description must be less than 500 characters' },
              })}
              size="small"
              fullWidth
              label="Description (Optional)"
              placeholder="Describe what this project is about..."
              multiline
              rows={3}
              error={!!errors.description}
              helperText={errors.description?.message}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={handleClose} 
            variant="outlined"
            size="medium"
          >
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={loading}
            size="medium"
            sx={{ minWidth: 100 }}
          >
            Create
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default CreateProjectDialog
